import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback } from "react";
import { MessageTypes } from "../Constants/MessageConstants";
import { MessageDeliveryType } from "../Enums/Message";
import { TemplateFieldType } from "../Enums/TemplateFieldType";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useNotification } from "../State/Hooks/notificationHooks";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { useUserProfileStore } from "../State/Stores/UserProfileStore";
import {
  ICandidateWithMessageInfo,
  IEmailTemplate,
  IGetCandidateScheduledMessagesParamaters,
  IGroupMessageViewResponse,
  IMessageCalendarConfigurations,
  IMessageCollection,
  IMessageCollectionDetail,
  IMessageDetail,
  IMessageEssentialParamaters,
  IMessageHistory,
  IMessageReadPost,
  IMessageScheduleDetail,
  IMessageSendPost,
  IMessageTextTemplateField,
  IPagination,
  ITextTemplate,
  IUpsertMessageTemplateCommand,
  MessageType,
  MessagingError,
  MessagingMetrics,
} from "../Typings/MessageText";
import { apiPathMessages, apiPathStopCompliance, fetcher, secureFetcher } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";

export const useSendScheduleMessageToApi = () => {
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  const { user } = useAuth0();
  const { displaySuccess, displayError } = useNotification();
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation<IMessageSendPost, unknown, IMessageSendPost>(
    (msgDetails: IMessageSendPost) => {
      const messageSendPostContract = {
        ...msgDetails,
        clientId,
        userId,
        userName: user?.name ?? "",
        notifyEmail: msgDetails.notify ? user?.email : "",
        messages: msgDetails.messages.map((message) => {
          return {
            ...message,
            hour: message.date.hour().toString(),
            minute: message.date.minute().toString(),
          } as IMessageScheduleDetail;
        }),
      };
      return fetcher(apiPathMessages + "send", {
        method: "POST",
        body: JSON.stringify(messageSendPostContract),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onError: (e) => {
        displayError("Unable to send message");
        console.error(e);
      },
      onSuccess: async () => {
        displaySuccess("Message sent");
        setTimeout(() => {
          queryClient.invalidateQueries(["candidateMessageHistory"]);
          queryClient.invalidateQueries(["getGroupScheduledMessages"]);
          queryClient.invalidateQueries(["scheduledMessageCollections"]);
          queryClient.invalidateQueries(["groupProfile"]);
        }, 2500);
        setTimeout(() => {
          queryClient.invalidateQueries(["allClients"]);
        }, 5000);
      },
      mutationKey: ["postMessageSendEndpoint"],
    },
  );
};

export const useGetGroupScheduledMessages = (groupId: string, isGroup: boolean) => {
  const selectedClient = useAppSelector(selectClientValue);
  const basePath = `${apiPathMessages}scheduled/client/`;
  const groupParams = `${selectedClient.clientId}/group/${groupId}/isgroup/${isGroup}`;
  const token = useAuthToken();

  return useQuery<IGroupMessageViewResponse>(
    ["getGroupScheduledMessages", { groupId, clientId: selectedClient.clientId, isGroup }],
    () => secureFetcher(`${basePath}${groupParams}`, token),
    {
      enabled: !!selectedClient.clientId && !!groupId && !!token,
      staleTime: 1000 * 10, // 10 seconds
    },
  );
};

export const useDeleteScheduledMessage = () => {
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { displaySuccess, displayError } = useNotification();
  const token = useAuthToken();

  return useMutation<string, unknown, string>(
    (msgTrackingId: string) => {
      return fetcher(apiPathMessages + `client/${clientId}/message/${msgTrackingId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onError: (e) => {
        displayError("Unable to delete message");
        console.error(e);
      },
      onSuccess: () => {
        displaySuccess("Message deleted");
        queryClient.invalidateQueries(["getGroupScheduledMessages"]);
        queryClient.invalidateQueries(["scheduledMessageCollections"]);
        queryClient.invalidateQueries(["candidateMessageHistory"]);
        queryClient.invalidateQueries(["groupProfile", clientId]);
        setTimeout(() => {
          queryClient.invalidateQueries(["allClients"]);
        }, 5000);
      },
      mutationKey: ["MessageDeleteEndpoint"],
    },
  );
};

const allTemplateFieldTypes = Object.values(TemplateFieldType);

export const useGetMessageTemplateFields = (
  excludedTypes: TemplateFieldType[] = [],
  excludedFields: string[] = [],
) => {
  const token = useAuthToken();
  const { profile } = useUserProfileStore();
  const filterTypes = excludedTypes !== undefined && excludedTypes.length > 0;
  const filterFields = excludedFields !== undefined && excludedFields.length > 0;
  const includedTypes = filterTypes
    ? allTemplateFieldTypes.filter((t) => !excludedTypes.includes(t))
    : allTemplateFieldTypes;

  const selectAvailableFields = useCallback(
    (data: IMessageTextTemplateField[]) => {
      const removeTitleField = Boolean(!profile?.title);
      return filterTypes || removeTitleField
        ? data.filter(
            (t) =>
              includedTypes.includes(t.templateType) &&
              (!removeTitleField || !t.field.toLowerCase().includes("title")) &&
              (!filterFields || !excludedFields.includes(t.field)),
          )
        : data;
    },
    [profile?.title],
  );

  return useQuery(
    ["messageTemplateFields"],
    () => secureFetcher(`${apiPathMessages}templates/fields`, token),
    {
      enabled:
        !!token &&
        (excludedTypes === undefined ||
          (excludedTypes.length > 0 && excludedTypes.every((t) => allTemplateFieldTypes.includes(t)))),
      select: excludedTypes ? selectAvailableFields : undefined,
      staleTime: 1000 * 60 * 60, // 1 hour
    },
  );
};

export const useGetMessageCalendarConfigs = (
  clientId: string,
  candidateId: string,
  messageType?: MessageType,
) => {
  const token = useAuthToken();
  return useQuery<IMessageCalendarConfigurations>(
    ["messageCalendarConfigs", messageType, candidateId, clientId],
    async () => {
      const url = `${apiPathMessages}scheduler/client/${clientId}/candidate/${candidateId}`;

      return (await secureFetcher(url, token)) as IMessageCalendarConfigurations;
    },
    {
      enabled:
        !!token &&
        (messageType === undefined || Object.values(MessageTypes).includes(messageType)) &&
        !!clientId &&
        !!candidateId,
    },
  );
};

export const useGetCandidatesWithMessageInfo = (
  page: number,
  pageSize: number,
  orderBy: MessageDeliveryType,
  search?: string,
  groupIds?: string[],
  unreadOnly?: boolean,
) => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery<IPagination<ICandidateWithMessageInfo>>(
    ["candidatesWithMessageInfo", clientId, page, pageSize, orderBy, search, groupIds, unreadOnly],
    async () => {
      let url = `candidates/messageinfo/${clientId}?page=${page}&pageSize=${pageSize}&deliveryMethod=${orderBy}&search=${search}&unreadOnly=${
        unreadOnly ?? false
      }`;

      if (groupIds && groupIds.length > 0) {
        url += `&groupIds=${groupIds.join(",")}`;
      }

      const response = (await secureFetcher(
        apiPathMessages + url,
        token,
      )) as IPagination<ICandidateWithMessageInfo>;

      return response;
    },
    {
      enabled: !!token,
    },
  );
};

export const useGetMessageDetail = (messageId: string, enabled = true) => {
  const token = useAuthToken();
  const { id: userId } = useCurrentUser();
  return useQuery<IMessageDetail>(
    ["messageInfo", messageId],
    () => secureFetcher(apiPathMessages + `detail/${messageId}?userId=${userId}`, token),
    {
      enabled: enabled && messageId.length > 0 && !!token,
    },
  );
};

export const useGetMessageCollectionDetail = (messageCollectionId: string) => {
  const token = useAuthToken();
  return useQuery<IMessageCollectionDetail>(
    ["messageCollectionInfo", messageCollectionId],
    () => secureFetcher(apiPathMessages + `detail/collection/${messageCollectionId}`, token),
    {
      enabled: messageCollectionId.length > 0 && !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );
};

export const useReadMessage = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, IMessageReadPost>(
    (messageRead) =>
      fetcher(apiPathMessages + "read", {
        method: "POST",
        body: JSON.stringify(messageRead),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidateMessageHistory"]);
        queryClient.invalidateQueries(["candidatesWithMessageInfo"]);
      },
      mutationKey: ["readMessage"],
    },
  );
};

export const useUnreadMessage = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  return useMutation<boolean, unknown, IMessageReadPost>(
    (messageRead) =>
      fetcher(apiPathMessages + "unread", {
        method: "POST",
        body: JSON.stringify(messageRead),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidateMessageHistory"]);
        queryClient.invalidateQueries(["candidatesWithMessageInfo"]);
      },
      mutationKey: ["unreadMessage"],
    },
  );
};

export const useCandidateStopCompliance = (candidateId: string) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<boolean>(
    ["candidateStopCompliance", candidateId],
    () =>
      secureFetcher(
        apiPathStopCompliance + "message/stopcheckstatus/client/" + clientId + "/candidate/" + candidateId,
        token,
      ),
    {
      enabled: !!clientId && !!candidateId && !!token,
      staleTime: 1000 * 6, //6 seconds
    },
  );
};

export const useCandidateMessageHistory = (
  candidateId: string,
  page: number,
  pageSize: number,
  deliveryMethod: MessageDeliveryType,
) => {
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  const token = useAuthToken();

  return useQuery<IPagination<IMessageHistory>>(
    ["candidateMessageHistory", clientId, candidateId, page, pageSize, deliveryMethod],
    async () => {
      const response = (await secureFetcher(
        `${apiPathMessages}history/client/${clientId}/candidate/${candidateId}?page=${page}&pageSize=${pageSize}&deliveryMethod=${deliveryMethod}&userId=${userId}`,
        token,
      )) as IPagination<IMessageHistory>;
      response.items = response.items.map((message) => convertMessageDates(message));

      return response;
    },
    {
      staleTime: 1000 * 5, // 5 seconds,
      enabled: !!clientId && !!candidateId && !!token,
      refetchInterval: 1000 * 5,
    },
  );
};

export const useGetScheduledMessages = () => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useMutation<IMessageEssentialParamaters, unknown, IMessageEssentialParamaters>(
    (params: IMessageEssentialParamaters) => {
      return token && clientId && params.groupId && params.isGroup
        ? secureFetcher(
            apiPathMessages +
              "scheduled/client/" +
              clientId +
              "/group/" +
              params.groupId +
              "/isgroup/" +
              params.isGroup,
            token,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            },
          )
        : Promise.resolve();
    },
    {
      onSuccess: (data: any, variables: any) => {
        //Nothing for now.
      },
      mutationKey: ["postMessageSendEndpoint"],
    },
  );
};

export const useDeleteMessageEmailTemplate = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { displaySuccess, displayError } = useNotification();
  return useMutation<boolean, unknown, string>(
    (emailTemplateId) =>
      fetcher(apiPathMessages + "templates/email/" + emailTemplateId, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSettled: (isSuccess, error) => {
        if (error || !isSuccess) {
          displayError("Error deleting email template.");
          console.error(error || "Server error");
        } else {
          displaySuccess("Email template deleted successfully.");
          queryClient.invalidateQueries(["clientEmailTemplates"]);
        }
      },
      mutationKey: ["deleteEmailTemplate"],
    },
  );
};

export const useDeleteMessageTextTemplate = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { displaySuccess, displayError } = useNotification();
  return useMutation<boolean, unknown, string>(
    (textTemplateId) =>
      fetcher(apiPathMessages + "templates/text/" + textTemplateId, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSettled: (isSuccess, error) => {
        if (error || !isSuccess) {
          displayError("Error deleting text template.");
          console.error(error || "Server error");
        } else {
          displaySuccess("Text template deleted successfully.");
          queryClient.invalidateQueries(["clientTextTemplates"]);
        }
      },
      mutationKey: ["deleteTextTemplate"],
    },
  );
};

export const useGetClientEmailTemplatesQuery = (search?: string) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useQuery<IEmailTemplate[]>(
    ["clientEmailTemplates", clientId, userId, search],
    async () => {
      const response: IEmailTemplate[] = await secureFetcher(
        apiPathMessages +
          "templates/email/client/" +
          clientId +
          "?userId=" +
          userId +
          (search ? `&search=${encodeURI(search)}` : ""),
        token,
      );
      return response.map((template) => ({
        ...template,
        visibility: template.isPublic ? "Public" : "Private",
      }));
    },
    {
      enabled: !!clientId && !!userId && !!token,
      staleTime: 1000 * 30, // 30 seconds
    },
  );
};

export const useGetClientTextTemplatesQuery = (search?: string) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useQuery<ITextTemplate[]>(
    ["clientTextTemplates", clientId, userId, search],
    async () => {
      const response: ITextTemplate[] = await secureFetcher(
        apiPathMessages +
          "templates/text/client/" +
          clientId +
          "?userId=" +
          userId +
          (search ? `&search=${encodeURI(search)}` : ""),
        token,
      );
      return response.map((template) => ({
        ...template,
        visibility: template.isPublic ? "Public" : "Private",
      }));
    },
    {
      enabled: !!clientId && !!userId && !!token,
      staleTime: 1000 * 30, // 30 seconds
    },
  );
};

export const useUpsertMessageTemplate = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  const { displayError, displaySuccess } = useNotification();
  return useMutation<boolean, unknown, IUpsertMessageTemplateCommand>(
    (command) =>
      fetcher(apiPathMessages + "templates/save", {
        method: "POST",
        body: JSON.stringify({
          ...command,
          userId: userId,
          clientId,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSettled: (isSuccess, error) => {
        if (error || !isSuccess) {
          displayError("Error saving template.");
          console.error(error || "Server error");
        } else {
          displaySuccess("Message template saved successfully.");
          queryClient.invalidateQueries(["clientEmailTemplates"]);
          queryClient.invalidateQueries(["clientTextTemplates"]);
        }
      },
      mutationKey: ["postUpsertMessageTemplate"],
    },
  );
};

export const useGetCandidateScheduledMessages = () => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useMutation<
    IGetCandidateScheduledMessagesParamaters,
    unknown,
    IGetCandidateScheduledMessagesParamaters
  >(
    (params: IGetCandidateScheduledMessagesParamaters) => {
      return secureFetcher(
        apiPathMessages + "candidatehistory/client/" + clientId + "/candidate/" + params.candidateId,
        token,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    },
    {
      onSuccess: (data: any, variables: any) => {
        //Nothing for now.
      },
      mutationKey: ["getIGetCandidateScheduledMessagesParamaters"],
    },
  );
};

export const useCandidateScheduledMessages = (candidateId: string) => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery<IGroupMessageViewResponse>(
    ["candidateScheduledMessages", clientId, candidateId],
    () =>
      secureFetcher(
        apiPathMessages + "candidatehistory/client/" + clientId + "/candidate/" + candidateId,
        token,
      ),
    {
      staleTime: 1000 * 10, // 10 seconds,
      enabled: !!clientId && !!candidateId && !!token,
    },
  );
};

/**
 * Converts dates from JSON string to Date objects
 *
 * @param message IMessageHistory with dates in string format
 * @returns IMessageHistory with date objects
 */
function convertMessageDates(message: IMessageHistory) {
  return {
    ...message,
    created: dayjs(message.created),
    scheduledUtcTime: dayjs(message.scheduledUtcTime),
    dateSort: dayjs(message.dateSort),
  } as IMessageHistory;
}

export const useGetMessagingMetrics = (
  token: string,
  startDate: Date,
  endDate: Date,
  error: boolean,
  deliveryMethod: MessageDeliveryType,
  clientIds: string[],
) => {
  return useQuery<MessagingMetrics[]>(
    ["messagingMetrics", startDate, endDate, error, deliveryMethod, clientIds],
    async () =>
      fetcher(
        apiPathMessages +
          `metrics?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&error=${error}&deliveryMethod=${deliveryMethod}&clientIds=${clientIds.join(
            "&clientIds=",
          )}`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      ),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes,
      enabled: !!token,
    },
  );
};

export const useGetMessagingErrors = (
  token: string,
  startDate: Date,
  endDate: Date,
  deliveryMethod: MessageDeliveryType,
  clientIds: string[],
) => {
  return useQuery<MessagingError[]>(
    ["messagingErrors", startDate, endDate, deliveryMethod, clientIds],
    async () =>
      fetcher(
        apiPathMessages +
          `errors?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&deliveryMethod=${deliveryMethod}&clientIds=${clientIds.join(
            "&clientIds=",
          )}`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      ),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes,
      enabled: !!token,
    },
  );
};

export const useGetScheduleMessageCollections = (groupId: string, page: number, pageSize: number) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);

  return useQuery<IPagination<IMessageCollection>>(
    ["scheduledMessageCollections", clientId, groupId],
    async () => {
      const url = `${apiPathMessages}scheduled/collection/client/${clientId}/group/${groupId}?page=${page}&pageSize=${pageSize}`;

      return (await secureFetcher(url, token)) as IPagination<IMessageCollection>;
    },
    {
      enabled: !!token,
    },
  );
};

export const useDeleteMessageCollection = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<boolean, unknown, string>(
    (messageCollectionId) =>
      fetcher(apiPathMessages + `client/${clientId}/collection/${messageCollectionId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: () => {
        setTimeout(() => {
          queryClient.invalidateQueries(["allClients"]);
          queryClient.invalidateQueries(["candidateMessageHistory"]);
          queryClient.invalidateQueries(["getGroupScheduledMessages"]);
          queryClient.invalidateQueries(["scheduledMessageCollections"]);
          queryClient.invalidateQueries(["groupProfile", clientId]);
        }, 5000);
      },
      mutationKey: ["deleteMessageColection"],
    },
  );
};
